import React, { useState, useMemo, useEffect } from 'react';
import { Container, Button, Grid, Icon, Form, Step, Segment, Message, Loader, Select, Checkbox } from 'semantic-ui-react';
import IVisitSlot from '../Models/IVisitSlot';
import OpeningHoursApi from '../api/OpeningHoursApi';
import BookingsApi from '../api/BookingsApi';
import Globals from '../Globals';
import QuotesApi from '../api/QuotesApi';
import { Controller, useForm } from 'react-hook-form';
import { IGroupBookingForm } from '../Models/IGroupBookingForm';
import dayjs from 'dayjs';
import 'dayjs/locale/fr'; // import locale
import { DataGouvApi } from '../api/DataGroupApi';
import Calendar from 'react-calendar';

dayjs.locale('fr');

const groupTypes = [
  { key: "sco", text: "Etablissement scolaire", value: "Etablissement scolaire" },
  { key: "loi", text: "Centre de loisirs", value: "Centre de loisirs" },
  { key: "the", text: "Centre thérapeutique", value: "Centre thérapeutique" },
  { key: "aut", text: "Autre", value: "Autre" },
];

const visitOptions = [
  { key: "quizz", text: "Quizz animaux", value: "Quizz animaux" },
  { key: "potager", text: "Potager", value: "Potager" },
  //  { key: "nourrissage", text: "Nourrissage", value: "Nourrissage" },
  //  { key: "autre", text: "Autre", value: "Autre (nous contacter)" },
];

const maxChildren = 80;
const minChildren = 15;

const minResaDate = dayjs().add(7, 'day');

const ReservationGroupes: React.FC = () => {
  const searchParams = new URLSearchParams(document.location.search);
  const bookingId = searchParams.get('b');
  const quoteId = searchParams.get('q');
  const [step, setStep] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [proceeding, setProceeding] = useState<boolean>(false);
  const [selectedDaySlotInfo, setSelectedDaySlotInfo] = useState<{ date: string, morningId: string, afternoonId: string }>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [visitSlots, setVisitSlots] = useState<IVisitSlot[]>([]);
  const [previousBooking, setPreviousBooking] = useState<IGroupBookingForm>();
  const [activeStartDate, setActiveStartDate] = useState<Date>(minResaDate.toDate());

  const { register, setValue, reset, control, handleSubmit, watch, formState: { errors } } = useForm<IGroupBookingForm>({
    mode: "onBlur",
    defaultValues: {
      firstname: (document.location.hostname === 'localhost' ? 'Gaetan' : ''),
      lastname: (document.location.hostname === 'localhost' ? 'Test' : ''),
      email: (document.location.hostname === 'localhost' ? 'test@fermerainbow.com' : ''),
      phone: (document.location.hostname === 'localhost' ? '0102030405' : ''),
      comments: '',
      picNic: false,
      selectedMonth: minResaDate.format('YYYY-MM'),
      options: []
    }
  });

  const status = watch("status");
  const groupName = watch("groupName");
  const zipCode = watch("zipCode");
  const city = watch("city");
  const groupType = watch("groupType");
  const firstname = watch("firstname");
  const lastname = watch("lastname");
  const email = watch("email");
  const phone = watch("phone");
  const children = watch("children");
  const adults = watch("adults");
  const averageAge = watch("averageAge");
  const selectedMonth = watch("selectedMonth");
  const itemIdMorning = watch("itemIdMorning");
  const itemIdAfternoon = watch("itemIdAfternoon");

  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return "Votre visite";
      case 2:
        return "Votre groupe";
      case 3:
        return "Vos coordonnées";
    }
  }, [step]);

  useEffect(() => {
    if (bookingId && quoteId && !watch("id")) {
      const qApi = new QuotesApi();
      qApi.getGroupBooking(bookingId, quoteId)
        .then((quoteResp) => {
          const quote = quoteResp.data;

          if (quote.quoteValidationDate) {
            setErrorMessage(`Vous ne pouvez plus mettre à jour le devis n°${quoteId} (déjà validé par retour signé)`);
            setStep(10);
            return;
          }
          if (quote.status === "REJECTED") {
            setErrorMessage(`Vous ne pouvez plus mettre à jour le devis n°${quoteId} (devis annulé)`);
            setStep(10);
            return;
          }
          if (quote.quoteDueDate) {
            if (dayjs(quote.quoteDueDate) < dayjs()) {
              setErrorMessage(`Vous ne pouvez plus mettre à jour le devis n°${quoteId} (date de validité dépassée)`);
              setStep(10);
              return;
            }
          }
          else {
            if (dayjs(quote.created).add(6) < dayjs()) {
              setErrorMessage(`Vous ne pouvez plus mettre à jour le devis n°${quoteId} (date de validité dépassée)`);
              setStep(10);
              return;
            }
          }

          const date = dayjs(quote.eventDate);
          const selMonth = date.format('YYYY-MM');
          const selDay = date.format('YYYY-MM-DD');
          const selDate = `${selDay} ${quote.itemIdMorning ? '10' : '14'}:00`;

          setActiveStartDate(date.toDate());

          const previousForm = {
            id: quote.id,
            bookingId: quote.bookingId,
            groupName: quote.groupName,
            groupType: quote.groupType,
            lastname: quote.lastname,
            firstname: quote.firstname,
            children: quote.children,
            adults: quote.adults,
            averageAge: quote.averageAge,
            city: quote.city,
            zipCode: quote.zipCode,
            comments: quote.comments,
            email: quote.email.trim(),
            phone: quote.phone,
            eventDate: quote.eventDate,
            itemIdAfternoon: quote.itemIdAfternoon,
            itemIdMorning: quote.itemIdMorning,
            options: quote.options ? quote.options.split(', ') : [],
            quoteId: quote.quoteId,
            status: quote.status,
            picNic: quote.picNic,
            selectedDate: selDate,
            selectedMonth: selMonth,
          } as IGroupBookingForm;

          setPreviousBooking(previousForm);

          reset(previousForm);
        });
    }
  }, [watch, bookingId, reset, quoteId]);

  useEffect(() => {
    if (zipCode && zipCode.length === 5) {
      if (zipCode === "93160") {
        setValue("city", "Noisy-le-Grand");
      }
      else {
        DataGouvApi.searchAddress(zipCode)
          .then((result) => {
            if (result.data && result.data.features && result.data.features.length > 0) {
              const firstCity = result.data.features.find(f => f.properties.type === 'municipality');
              if (firstCity) {
                setValue("city", firstCity.properties.name);
              }
              else {
                console.log('city not found from zipcode in results');
              }
            }
            else {
              console.log('city not found from zipcode (no results)');
            }
          })
          .catch(() => {
            console.log('city not found from zipcode');
          });
      }
    }
  }, [zipCode, setValue]);

  useEffect(() => {
    if (selectedMonth) {
      setLoading(true);
      const oApi = new OpeningHoursApi();
      const start = dayjs(`${selectedMonth}-01`);
      const end = start.add(1, 'month');
      Promise.all([
        oApi.getGroupsBookingOpeningHours(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')),
        oApi.getReservedGroupsBookings(start.format('YYYY-MM-DD'))
      ]).then((res) => {
        let finalSlots: IVisitSlot[] = [];
        let unavailableSlotIds = res[1].data;
        if (previousBooking) {
          unavailableSlotIds = unavailableSlotIds.filter(s => s !== previousBooking.itemIdMorning && s !== previousBooking.itemIdAfternoon)
        }
        res[0].data.forEach((s) => {
          s.available = (unavailableSlotIds.indexOf(s.id) < 0);
          if (s.available) finalSlots.push(s);
        });
        setVisitSlots(finalSlots);
      }).catch(() => {
        setVisitSlots([]);
      })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [previousBooking, selectedMonth]);

  const currentVisitOptions = useMemo(() => {
    if (!selectedMonth) return [];

    const cMonth = dayjs(`${selectedMonth}-01`).month();
    console.log(cMonth);
    if (cMonth < 2 || cMonth > 8)
      return visitOptions.filter(v => v.key !== "potager");
    else
      return visitOptions;
  }, [selectedMonth]);

  const morningIsSelected = useMemo(() => {
    return selectedDaySlotInfo && !itemIdAfternoon && itemIdMorning === selectedDaySlotInfo.morningId;
  }, [selectedDaySlotInfo, itemIdAfternoon, itemIdMorning])

  const afternoonIsSelected = useMemo(() => {
    return selectedDaySlotInfo && !itemIdMorning && itemIdAfternoon === selectedDaySlotInfo.afternoonId;
  }, [selectedDaySlotInfo, itemIdAfternoon, itemIdMorning])

  const stepInfoIsValid = useMemo<boolean>(() => {
    return (!!groupName && !!groupType && !!lastname && !!firstname && !!city && !!phone && !!zipCode && !!email && !errors.email && !errors.zipCode);
  }, [groupName, groupType, lastname, firstname, city, zipCode, phone, email, errors]);

  const onSubmit = (data: IGroupBookingForm) => {
    setErrorMessage(undefined);
    setProceeding(true);

    const eventDate = dayjs(data.selectedDate).set('hour', data.itemIdMorning ? 10 : 14).set('minute', 0).toDate();

    const bApi = new BookingsApi();

    if (status) {
      bApi.updateGroupBookingRequest(data.id, {
        firstname: data.firstname,
        lastname: data.lastname,
        groupName: data.groupName,
        groupType: data.groupType,
        email: data.email.trim(),
        phone: data.phone,
        children: parseInt(data.children.toString()),
        adults: parseInt(data.adults.toString()),
        averageAge: parseInt(data.averageAge.toString()),
        city: data.city,
        zipCode: data.zipCode,
        itemIdMorning: data.itemIdMorning,
        itemIdAfternoon: data.itemIdAfternoon,
        options: data.options ? data.options.join(', ') : "",
        eventDate: eventDate,
        comments: data.comments,
        picNic: data.picNic,
        bookingId,
        quoteId
      }).then((res) => {
        setProceeding(false);
        setStep(5);
      }).catch((reason) => {
        if (reason.message && reason.message.indexOf('409') >= 0)
          setErrorMessage('Une erreur est survenue, il semblerait que ce créneau ait été réservé entre-temps. Veuillez en choisir un autre.');
        else
          setErrorMessage('Une erreur est survenue');
        setProceeding(false);
      });
    }
    else {
      bApi.createGroupBookingRequest({
        firstname: data.firstname,
        lastname: data.lastname,
        groupName: data.groupName,
        groupType: data.groupType,
        email: data.email.trim(),
        phone: data.phone,
        children: parseInt(data.children.toString()),
        adults: parseInt(data.adults.toString()),
        averageAge: parseInt(data.averageAge.toString()),
        city: data.city,
        zipCode: data.zipCode,
        itemIdMorning: data.itemIdMorning,
        itemIdAfternoon: data.itemIdAfternoon,
        options: data.options ? data.options.join(', ') : "",
        eventDate: eventDate,
        comments: data.comments,
        picNic: data.picNic,
        bookingId: null,
        quoteId: null,
      }).then((res) => {
        setProceeding(false);
        setStep(4);
      }).catch((reason) => {
        if (reason.message && reason.message.indexOf('409') >= 0)
          setErrorMessage('Une erreur est survenue, il semblerait que ce créneau ait été réservé entre-temps. Veuillez en choisir un autre.');
        else
          setErrorMessage('Une erreur est survenue');
        setProceeding(false);
      });
    }
  };

  return (
    <Container fluid>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {step <= 4 && (
          <Step.Group widths="three" size="tiny">
            <Step active={step === 1} completed={step > 1} icon="calendar alternate outline" title="Votre visite" />
            <Step active={step === 2} completed={step > 2} icon="users" title="Votre groupe" />
            <Step active={step === 2} completed={step > 3} icon="address card" title="Vos coordonnées" />
          </Step.Group>
        )}
        {previousBooking && (<>
          <h3>Demande de mise à jour de votre devis n°{quoteId}</h3>
          <div>Votre précédent devis concernait une visite le {dayjs(previousBooking.eventDate).format("DD MMMM YYYY")} {(previousBooking.itemIdMorning ? "matin" : "après-midi")}.</div>
        </>)}
        <h4>{stepTitle}</h4>
        {visitSlots && step === 1 && (
          <>
            <Grid stackable>
              <Grid.Row columns="2">
                <Grid.Column textAlign='center'
                  style={{ maxWidth: '400px' }}>
                  <Calendar
                    view={"month"}
                    activeStartDate={activeStartDate}
                    showNeighboringMonth={false}
                    showDoubleView={false}
                    onActiveStartDateChange={(v) => {
                      if (v.activeStartDate) {
                        setValue('selectedMonth', dayjs(v.activeStartDate).format('YYYY-MM'));
                        setActiveStartDate(v.activeStartDate);
                      }
                    }}
                    className={"calendar-group"}
                    minDate={minResaDate.toDate()}
                    maxDate={minResaDate.add(1, 'year').toDate()}
                    tileDisabled={({ activeStartDate, date, view }) => {
                      const day = dayjs(date).format('YYYY-MM-DD');
                      return (visitSlots.filter(s => s.date.indexOf(day) === 0)).length === 0;
                    }}
                    onClickDay={(value) => {
                      let day = "";
                      let morningId = "";
                      let afternoonId = "";

                      if (value) {
                        //setValue("selectedDate", curDate);
                        day = dayjs(value).format('YYYY-MM-DD');
                        const selDays = visitSlots.filter(s => s.date.indexOf(day) === 0);
                        if (selDays) {
                          selDays.forEach((d) => {
                            if (d.startHour < 12) {
                              morningId = d.id;
                            }
                            else {
                              afternoonId = d.id;
                            }
                          });
                        }
                      }

                      setSelectedDaySlotInfo({
                        date: day,
                        morningId: morningId,
                        afternoonId: afternoonId,
                      });
                    }}
                    tileClassName={({ date, view }) => `day-slots-${visitSlots.filter(s => s.date.indexOf(dayjs(date).format('YYYY-MM-DD')) === 0).length}`}
                  />
                </Grid.Column>
                <Grid.Column verticalAlign={'middle'} textAlign='center'>
                  {loading ? (
                    <Segment textAlign='center'
                      style={{ maxWidth: '400px' }}>
                      <Loader active inline size='small' /> &nbsp; Chargement des disponibilités...
                    </Segment>
                  ) : (
                    <div
                      style={{ maxWidth: '400px', display: 'flex', justifyItems: "middle", flexDirection: 'column', rowGap: 15 }}>
                      <Button
                        color='blue'
                        inverted={!selectedDaySlotInfo || !selectedDaySlotInfo.morningId || itemIdMorning !== selectedDaySlotInfo.morningId}
                        fluid
                        icon={morningIsSelected}
                        labelPosition={morningIsSelected ? "right" : undefined}
                        disabled={!selectedDaySlotInfo || !selectedDaySlotInfo.morningId}
                        onClick={(ev) => {
                          ev.preventDefault();
                          setValue('selectedDate', selectedDaySlotInfo!.date);
                          setValue("itemIdMorning", selectedDaySlotInfo!.morningId);
                          setValue("itemIdAfternoon", '');
                          setValue("picNic", false);
                        }}>
                        {morningIsSelected && <Icon name="checkmark" style={{ marginRight: 3 }}></Icon>}
                        {selectedDaySlotInfo ? dayjs(selectedDaySlotInfo.date).format('DD MMMM') : ''} Matin</Button>
                      <Button
                        color='blue'
                        inverted={!selectedDaySlotInfo || !selectedDaySlotInfo.afternoonId || itemIdAfternoon !== selectedDaySlotInfo.afternoonId}
                        fluid
                        icon={afternoonIsSelected}
                        labelPosition={afternoonIsSelected ? "right" : undefined}
                        disabled={!selectedDaySlotInfo || !selectedDaySlotInfo.afternoonId}
                        onClick={(ev) => {
                          ev.preventDefault();
                          setValue('selectedDate', selectedDaySlotInfo!.date);
                          setValue("itemIdMorning", '');
                          setValue("itemIdAfternoon", selectedDaySlotInfo!.afternoonId);
                          setValue("picNic", false);
                        }}>
                        {afternoonIsSelected && <Icon name="checkmark" style={{ marginRight: 3 }}></Icon>}
                        {selectedDaySlotInfo ? dayjs(selectedDaySlotInfo.date).format('DD MMMM') : ''} Après-midi</Button>
                      <Form.Field style={{ margin: 0 }}>
                        <label>Activités optionnelles</label>
                        <Controller
                          control={control}
                          name="options"
                          render={({ field: { value } }) => (
                            <Select options={currentVisitOptions} value={value} multiple onChange={(ev, { value }) => setValue("options", value as string[])} fluid />
                          )}
                        />
                      </Form.Field>
                      <Form.Field>
                        <Controller
                          control={control}
                          name="picNic"
                          render={({ field: { value } }) => (
                            <Checkbox label="Pique-nique sur place" checked={value} disabled={!itemIdMorning} onChange={(ev, { checked }) => setValue("picNic", checked !== undefined ? checked : false)} />
                          )}
                        />
                        <Message size="mini" negative={false}><b>Important:</b> votre groupe devra quitter les lieux à 13h15 afin de permettre l'accueil du groupe de l'après-midi.</Message>
                      </Form.Field>
                    </div>)}
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <Grid>
              <Grid.Row columns="2">
                <Grid.Column>
                </Grid.Column>
                <Grid.Column textAlign="right">
                  <Button size='medium' color='green' icon labelPosition='right' onClick={() => { setStep(2); }} disabled={!itemIdAfternoon && !itemIdMorning}><Icon name='users' /> Etape suivante - Votre groupe</Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </>
        )}
        {step === 2 && (
          <>
            <Form.Group widths="equal">
              <Form.Field required>
                <label>Nombre d'enfants (entre {minChildren} et {maxChildren})</label>
                <input type="number" {...register("children", { required: true, max: maxChildren, min: minChildren, pattern: /^\d+$/ })} min={minChildren} max={maxChildren} />
                {errors.children && <Message size="mini" negative={true}>Ce champ est obligatoire, avec un maximum de {maxChildren} enfants.</Message>}
              </Form.Field>
              <Form.Field required>
                <label>Age moyen des enfants</label>
                <input type="number" {...register("averageAge", { required: true, pattern: /^\d+$/ })} />
                {errors.averageAge && <Message size="mini" negative={true}>Ce champ est obligatoire.</Message>}
              </Form.Field>
              <Form.Field required>
                <label>Nombre d'accompagnants</label>
                <input type="number" {...register("adults", { required: true, max: maxChildren, pattern: /^\d+$/ })} max={maxChildren} />
                {errors.children && <Message size="mini" negative={true}>Ce champ est obligatoire, avec un maximum de {maxChildren} accompagnants.</Message>}
              </Form.Field>
            </Form.Group>
            <Grid>
              <Grid.Row columns="2">
                <Grid.Column>
                  <Button size='small' icon labelPosition='left' onClick={() => { setStep(1); }}><Icon name='address card' /> Etape précédente - Votre visite</Button>
                </Grid.Column>
                <Grid.Column textAlign="right">
                  <Button size="medium" color='green' icon labelPosition='right' disabled={!adults || !children || !averageAge || !!errors.children} onClick={() => { setStep(3); }}><Icon name='calendar alternate outline' /> Etape suivante - Vos coordonnées</Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </>
        )}
        {step === 3 && (
          <Grid stackable>
            <Grid.Row>
              <Grid.Column>
                <Form.Group widths="equal">
                  <Form.Field required>
                    <label>Nom de l'établissement</label>
                    <input {...register("groupName", { required: true })} />
                    {errors.groupName && <Message size="mini" negative={true}>Ce champ est obligatoire</Message>}
                  </Form.Field>
                  <Form.Field required>
                    <label>Type d'établissement</label>
                    <Controller
                      control={control}
                      name="groupType"
                      rules={{ required: true }}
                      render={({ field: { value } }) => (
                        <Select options={groupTypes} value={value} onChange={(ev, data) => setValue("groupType", data.value as string)} fluid />
                      )}
                    />
                    {errors.groupType && <Message size="mini" negative={true}>Ce champ est obligatoire</Message>}
                  </Form.Field>
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Field required>
                    <label>Code postal</label>
                    <input maxLength={5} {...register("zipCode", { required: true, pattern: /^\d{5}$/ })} />
                    {errors.zipCode && <Message size="mini" negative={true}>Le code postal n'est pas valide (doit être composé de 5 caractères numériques).</Message>}
                  </Form.Field>
                  <Form.Field required>
                    <label>Ville</label>
                    <input {...register("city", { required: true })} />
                    {errors.city && <Message size="mini" negative={true}>Ce champ est obligatoire</Message>}
                  </Form.Field>
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Field required>
                    <label>Votre nom</label>
                    <input {...register("lastname", { required: true })} />
                    {errors.lastname && <Message size="mini" negative={true}>Ce champ est obligatoire</Message>}
                  </Form.Field>
                  <Form.Field required>
                    <label>Votre prénom</label>
                    <input {...register("firstname", { required: true })} />
                    {errors.firstname && <Message size="mini" negative={true}>Ce champ est obligatoire</Message>}
                  </Form.Field>
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Field required>
                    <label>Téléphone</label>
                    <input {...register("phone", { required: true })} />
                    {errors.phone && <Message size="mini" negative={true}>Ce champ est obligatoire</Message>}
                  </Form.Field>
                  <Form.Field required>
                    <label>Adresse email</label>
                    <input {...register("email", { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })} />
                    {errors.email && <Message size="mini" negative={true}>Email invalide ou manquant</Message>}
                  </Form.Field>
                </Form.Group>
                <Form.Field>
                  <label>Commentaires</label>
                  <textarea rows={5} style={{ minHeight: 100, width: '100%' }} {...register("comments")} />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns="2">
              <Grid.Column>
                <Button size='medium' icon labelPosition='left' onClick={() => { setStep(2); }}><Icon name='users' /> Etape précédente - Votre groupe</Button>
              </Grid.Column>
              <Grid.Column textAlign="right">
                <Button size="medium" color='green' icon labelPosition='right' loading={proceeding} disabled={stepInfoIsValid === false || proceeding} type="submit"><Icon name='check circle' /> {!!status ? "Mettre à jour" : "Envoyer"}</Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </Form>
      {
        step === 4 && (
          <>
            <Segment color="green" inverted>
              <p>Votre demande a été envoyée !</p>
            </Segment>
            <p>Vous recevrez une confirmation par mail de la part de "reservations@fermerainbow.com" contenant un rappel de votre demande et notre devis accompagné de nos CGV.<br />En cas de non réception, vérifiez dans vos spams et le cas échéant contactez-nous sur {Globals.Email}</p>
            <p className="noprint"><Button onClick={() => { document.location.assign('/') }} color="green">Retourner à l'accueil du site</Button></p>
          </>
        )
      }
      {
        step === 5 && (
          <>
            <Segment color="green" inverted>
              <p>Votre demande de mise à jour de devis a été envoyée !</p>
            </Segment>
            <p>Vous recevrez une confirmation par mail de la part de "reservations@fermerainbow.com" contenant un rappel de votre demande et notre devis accompagné de nos CGV.<br />En cas de non réception, vérifiez dans vos spams et le cas échéant contactez-nous sur {Globals.Email}</p>
            <p className="noprint"><Button onClick={() => { document.location.assign('/') }} color="green">Retourner à l'accueil du site</Button></p>
          </>
        )
      }
      {
        step === 6 && (
          <>
            <Segment color="orange" inverted>
              <p>Votre demande de devis a été annulée et ne peut être changée.</p>
            </Segment>
            <p>Vous pouvez effectuer une nouvelle demande de devis sur notre formulaire.</p>
            <p className="noprint"><Button onClick={() => { document.location.assign('/') }} color="green">Retourner à l'accueil du site</Button></p>
          </>
        )
      }
      <Message negative={true} hidden={!errorMessage}>{errorMessage}</Message>
    </Container >
  );
}

export default ReservationGroupes;
